// GitHub OAuth2 — Authorization Code flow (no PKCE for OAuth Apps)
//
// Offline-first notes:
//   - GitHub access tokens do NOT expire by default → zero refresh burden
//   - Token stored in IndexedDB, survives app restarts
//   - When offline, cached data is served; token is never validated against network
//
// App requirements (user registers their own GitHub OAuth App):
//   - client_id   — from GitHub OAuth App settings
//   - client_secret — from GitHub OAuth App settings (safe in Electron desktop app)
//   - redirect_uri — verigit://oauth/callback (Electron) or <origin>/oauth/callback
//
// Endpoints:
//   Auth URL  : https://github.com/login/oauth/authorize
//   Token URL : https://github.com/login/oauth/access_token
//   User URL  : https://api.github.com/user
//
// Default scopes: repo read:org read:user user:email

import {
  generateRandomString,
  isElectron,
  proxyUrl,
  parseCallbackUrl,
  openElectronOAuth2Popup,
  openBrowserOAuth2Popup,
  getOAuth2RedirectUri,
  EXPIRY_BUFFER_MS,
} from './platform-oauth2';
import type { PlatformOAuth2Token, PlatformOAuthState } from './types';
import {
  getPlatformOAuth2Token,
  savePlatformOAuth2Token,
  clearPlatformOAuth2Token,
} from './storage';

const GITHUB_AUTH_URL = 'https://github.com/login/oauth/authorize';
const GITHUB_TOKEN_URL = 'https://github.com/login/oauth/access_token';
const GITHUB_USER_URL = 'https://api.github.com/user';

export const GITHUB_OAUTH_SCOPES = 'repo read:org read:user user:email';

const PLATFORM = 'github' as const;

// ---------------------------------------------------------------------------
// GitHubOAuth2Service
// ---------------------------------------------------------------------------

class GitHubOAuth2Service {
  private static instance: GitHubOAuth2Service;
  private clientId = '';
  private clientSecret = '';
  private redirectUri = '';
  private stateCallbacks: Set<(state: PlatformOAuthState) => void> = new Set();

  private constructor() {}

  static getInstance(): GitHubOAuth2Service {
    if (!GitHubOAuth2Service.instance) {
      GitHubOAuth2Service.instance = new GitHubOAuth2Service();
    }
    return GitHubOAuth2Service.instance;
  }

  /**
   * Configure with OAuth App credentials.
   * redirectUri defaults to the environment-appropriate value if omitted.
   */
  configure(clientId: string, clientSecret: string, redirectUri?: string): void {
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.redirectUri = redirectUri ?? getOAuth2RedirectUri();
    console.log('[GitHubOAuth2] Configured', { clientId, redirectUri: this.redirectUri });
  }

  onStateChange(cb: (state: PlatformOAuthState) => void): () => void {
    this.stateCallbacks.add(cb);
    return () => this.stateCallbacks.delete(cb);
  }

  private notifyStateChange(state: PlatformOAuthState): void {
    this.stateCallbacks.forEach((cb) => {
      try { cb(state); } catch (e) { console.error('[GitHubOAuth2] State callback error:', e); }
    });
  }

  private computeState(token: PlatformOAuth2Token | null): PlatformOAuthState {
    if (!token) {
      return {
        isAuthenticated: false,
        isExpired: false,
        canRefresh: false,
        isOfflineMode: !navigator.onLine,
        expiresInMs: 0,
      };
    }
    // GitHub tokens don't expire (expiresAt === 0 means no expiry)
    const now = Date.now();
    const expiresInMs =
      token.expiresAt === 0 ? Number.POSITIVE_INFINITY : token.expiresAt - now - EXPIRY_BUFFER_MS;
    return {
      isAuthenticated: true,
      isExpired: token.expiresAt !== 0 && expiresInMs <= 0,
      canRefresh: !!token.refreshToken,
      isOfflineMode: !navigator.onLine,
      expiresInMs,
      token,
    };
  }

  async getState(): Promise<PlatformOAuthState> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    return this.computeState(token);
  }

  // ---------------------------------------------------------------------------
  // Auth flow
  // ---------------------------------------------------------------------------

  private buildAuthUrl(): { url: string; state: string } {
    if (!this.clientId) {
      throw new Error(
        'GitHubOAuth2Service not configured — call configure() first with your GitHub OAuth App credentials'
      );
    }
    const stateParam = generateRandomString(32);
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: GITHUB_OAUTH_SCOPES,
      state: stateParam,
    });
    return { url: `${GITHUB_AUTH_URL}?${params.toString()}`, state: stateParam };
  }

  private async exchangeCodeForTokens(code: string): Promise<PlatformOAuth2Token> {
    console.log('[GitHubOAuth2] Exchanging code for tokens...');

    const body = new URLSearchParams({
      client_id: this.clientId,
      client_secret: this.clientSecret,
      code,
      redirect_uri: this.redirectUri,
    });

    const fetchUrl = isElectron ? GITHUB_TOKEN_URL : proxyUrl(GITHUB_TOKEN_URL);
    const response = await fetch(fetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`GitHub token exchange failed (${response.status}): ${text}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`GitHub OAuth error: ${data.error_description ?? data.error}`);
    }

    const now = Date.now();
    let userId: string | undefined;
    let displayName: string | undefined;
    let email: string | undefined;
    let username: string | undefined;
    let avatarUrl: string | undefined;

    try {
      const userFetchUrl = isElectron ? GITHUB_USER_URL : proxyUrl(GITHUB_USER_URL);
      const userResp = await fetch(userFetchUrl, {
        headers: { Authorization: `token ${data.access_token}` },
      });
      if (userResp.ok) {
        const user = await userResp.json();
        userId = String(user.id);
        displayName = user.name ?? user.login;
        email = user.email ?? undefined;
        username = user.login;
        avatarUrl = user.avatar_url;
        console.log('[GitHubOAuth2] User profile:', { userId, username, displayName });
      }
    } catch (e) {
      console.warn('[GitHubOAuth2] Could not fetch user info:', e);
    }

    const token: PlatformOAuth2Token = {
      id: `${PLATFORM}-oauth2`,
      platform: PLATFORM,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      // GitHub tokens don't expire by default; use 0 to indicate no expiry
      expiresAt: data.expires_in ? now + data.expires_in * 1000 : 0,
      scopes: (data.scope ?? GITHUB_OAUTH_SCOPES).split(/[, ]+/),
      tokenType: data.token_type ?? 'token',
      userId,
      displayName,
      email,
      username,
      avatarUrl,
      issuedAt: now,
      offlineCapable: true,
    };

    await savePlatformOAuth2Token(token);
    this.notifyStateChange(this.computeState(token));
    console.log('[GitHubOAuth2] Token saved. ExpiresAt:', token.expiresAt === 0 ? 'never' : new Date(token.expiresAt).toISOString());
    return token;
  }

  async initiateFlow(): Promise<PlatformOAuth2Token> {
    const { url, state } = this.buildAuthUrl();
    let callbackUrl: string;
    if (isElectron) {
      callbackUrl = await openElectronOAuth2Popup(url, this.redirectUri);
    } else {
      callbackUrl = await openBrowserOAuth2Popup(url, 'github-oauth2-callback');
    }
    const { code } = parseCallbackUrl(callbackUrl, state);
    return this.exchangeCodeForTokens(code);
  }

  // ---------------------------------------------------------------------------
  // Token access
  // ---------------------------------------------------------------------------

  /**
   * Returns the current access token.
   * GitHub tokens don't expire, so no refresh logic is needed.
   */
  async getAccessToken(): Promise<string | null> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    return token?.accessToken ?? null;
  }

  async revokeToken(): Promise<void> {
    await clearPlatformOAuth2Token(PLATFORM);
    this.notifyStateChange(this.computeState(null));
    console.log('[GitHubOAuth2] Token revoked and cleared from storage');
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const githubOAuth2 = GitHubOAuth2Service.getInstance();
